<template>
    <div :style="{'max-height': `${computedMaxHeight}px`}" class="scrollBoxBoth">
        <div ref="editor-frame" id="editor-frame" v-bind:style="frameStyles">
            <div id="editor-canvas"
                 ref="editorCanvas"
                 :class="{'dropzone-readonly': basicPrivilege}"
                 class="browsershot-container"
                 @click.ctrl="onCanvasCtrlClick"
                 @click="onCanvasClick"
            >
                <template v-for="(element, key, index) in template.elements">
                    <elementInTemplate
                        :ref="`element${key}`"
                        v-model="active_element"
                        :class="{
                            'template-background-element': key == 0
                        }"
                        :index="key"
                        :key="'element-' + key"
                        :data="element"
                        :frame="frame"
                        :basic-privilege="basicPrivilege"
                        :extendedInfo="false"
                        :id="'element-' + key"
                        :interactive="isElementInteractive(key)"
                        @widthActive="activeElementWidth = $event"
                        @heightActive="activeElementHeight = $event"
                    />
                </template>

                <div v-if="actionButtonsShown"
                     ref="elementActions"
                     :style="actionButtonStyles"
                     class="element-action-buttons outlined">
                    <template v-if="activeIcons(template.elements[active_element])">
                        <!-- Note: stop propagation by click.stop is necessary. Especially for copyElement, which gets mixed up when click is propagated and tap event is executed simultaneously (Original comment by Bene in commit: e976425abd878e93b8254ea9cfcc8ce5e8ae82fd) -->
                        <a v-if="elementEditButtonShown"
                           aria-label="edit"
                           class="btn btn-xs btn-default"
                           href="#"
                           @click.stop="onElementEditButtonClick">
                            <i class="far fa-pencil fa-fw"></i>
                        </a>
                        <a aria-label="up"
                           class="btn btn-xs btn-default"
                           href="#"
                           @click.stop="$refs[`element${active_element}`][0].zindexUp()">
                            <i class="fa-regular fa-arrow-up fa-fw"></i>
                        </a>
                        <a aria-label="down"
                           class="btn btn-xs btn-default"
                           href="#"
                           @click.stop="$refs[`element${active_element}`][0].zindexDown()">
                            <i class="fa-regular fa-arrow-down fa-fw"></i>
                        </a>
                        <a aria-label="maximize"
                           class="btn btn-xs btn-default"
                           href="#"
                           @click.stop="$refs[`element${active_element}`][0].maximizeElement()">
                            <i class="fa-regular fa-arrows-maximize fa-fw"></i>
                        </a>
                        <a aria-label="clone"
                           class="btn btn-xs btn-default"
                           href="#"
                           @click.stop="$refs[`element${active_element}`][0].copyElement()">
                            <i class="fa-regular fa-clone fa-fw"></i>
                        </a>
                        <a aria-label="trash"
                           class="btn btn-xs btn-default"
                           href="#"
                           @click.stop="$refs[`element${active_element}`][0].deleteElement()">
                            <i class="fa-regular fa-trash fa-fw"></i>
                        </a>
                    </template>
                    <template v-else-if="template.elements[active_element].locked">
                        <a aria-label="locked" class="btn btn-xs btn-default element-locked-icon">
                            <i class="fa-regular fa-lock fa-fw"></i>
                        </a>
                    </template>

                </div>
            </div>
            <preview :url="preview_url" :aspect="template.parameters.format" :resolution="template.parameters.resolution"></preview>
        </div>
    </div>
</template>

<script>
    import preview from '../common/preview.vue';
    import debounce from 'debounce';
    import Combokeys from 'combokeys';
    import elementInTemplate from './element.vue'

    import {elementsSettingsDefaults, elementsSettingsLimits, elementsSettingsDefinition} from '../settings/settingsDefinition.js';
    import toaster from "../../common/toaster";

    export default {
        components: {
            preview,
            elementInTemplate
        },
        props: [
            'data',
            'value',
            'grid',
            'basicPrivilege',
            'advancedPrivilege',
            'preview',
            'inline',
            'add',
            'insertX',
            'insertY',
            'parent_layer_limitation',
            'parent_usergroup_limitation',
            'headerHeight'
        ],
        data: function () {
            return {
                t_add_failed: Lang.langGet("Failed to add an element."),
                t_edit_failed_prefix: Lang.langGet("Failed to edit template:"),
                t_preview_failed: Lang.langGet("Failed to load preview!"),

                frame: {
                    width: 1920,
                    height: 1080,
                    ratio: 1,
                    default_ratio: 0.5625,
                    default_size: 600,
                    default_position: 0,
                },
                active_element: 0,
                template: {
                    elements: [
                        {
                            type: 'background',
                            background_image: '/img/shim.png',
                            position: {
                                left: 0,
                                top: 0,
                                zindex: 1
                            },
                            size: {
                                width: 1,
                                height: 1,
                                ratio: 0
                            },
                            usergroups: [],
                            layers: [],
                            tags: [],
                            locked: false,
                            parameters: {
                                resolution: '',
                                format: '',
                                'valid-from': '',
                                'valid-to': ''
                            },
                            unique_id: '',
                        }
                    ],
                    parameters: {
                        resolution: '',
                        format: '',
                        'valid-from': '',
                        'valid-to': ''
                    },
                    presets: {},
                },
                highest_zindex: 1,
                preview_url: '',
                link_id: null,
                all_layers: [],
                previousParameters: '',
                resolutionWidth: 1920,
                resolutionHeight: 1080,
                computedMaxWidth: 0,
                computedMaxHeight: 0,
                zoom: 1,
                parentLayerLimitation: [],
                parentUsergroupLimitation: [],

                currentLayerLimitation: [],
                currentElementUsergroupLimitation: [],
                unsavedChanges: false,
                queued_structures: [],

                stepHorizontal: 0,
                stepVertical: 0,
                shiftKeyStatus: false,
                sendKeyEvent: false,

                canvasClickCooldown: false,
                ctrlDrilledMode: false,

                activeElementWidth: 0,
                activeElementHeight: 0,
                activeElementPositionTop: 0,
                activeElementPositionLeft: 0,
                actionButtonsWidth: 0,
                actionButtonsHeight: 0,
                actionButtonsPadding: 4,
                actionButtonsHeightInner: 72,
                actionButtonsHeightOuter: 32,
                actionsResizeObserver: null,
                elementEditButtonShown: false,
            }
        },
        computed: {
            frameStyles: function () {
                let width = this.frame.width,
                    height = this.frame.height,
                    margin = '';

                if (this.preview === 'true') {
                    margin = 'auto';
                }

                return {
                    width: `${width + 4}px`, // 2px padding on each side
                    height: `${height + 4}px`, // 2px padding on each side
                    margin: `${margin}`
                }
            },
            numColsCanvas: function () {
                if (this.grid === '2cols') {
                    return 8;
                } else {
                    return 5;
                }
            },
            activeUsergroups: function () {
                return this.template.elements[this.active_element].usergroups;
            },
            templateUsergroupsStrict: function () {
                return this.template.elements[0].strict_mode;
            },
            templateLayers: function () {
                return this.template.layers;
            },
            templateElements: function () {
                return this.template.elements;
            },
            activeElementWidthSmallerThanActions() {
                return this.activeElementWidth < (this.actionButtonsWidth + 2 * this.actionButtonsPadding);
            },
            activeElementHeightSmallerThanActions() {
                return this.activeElementHeight < this.actionButtonsHeightInner;
            },
            computedActionsOutside() {
                return this.activeElementWidthSmallerThanActions || this.activeElementHeightSmallerThanActions;
            },
            positionTooHighForActions() {
                return this.computedActionsOutside && (this.activeElementPositionTop < this.actionButtonsHeightOuter);
            },
            positionTooLeftForActions() {
                return (this.computedActionsOutside && ((this.activeElementPositionLeft + this.activeElementWidth) < (this.actionButtonsWidth + ((this.elementTooTallForActionsOutside ? 2 : 1) * this.actionButtonsPadding))));
            },
            positionTooRightForActions() {
                // Returns number of pixels of diff between element right edge and frame right edge,
                // WHEN the position of element would cause the actions to be drawn too right.
                // Basically, number of pixels of extra padding required on element actions to
                // keep "actionButtonsPadding" between actions right edge and editor frame right edge.
                // Otherwise, returns 0.
                return Math.max(this.computedActionsOutside && ((this.activeElementPositionLeft + this.activeElementWidth + this.actionButtonsPadding) - (this.frame.width)), 0);
            },
            elementTooTallForActionsOutside() {
                return (this.activeElementPositionTop < this.actionButtonsHeightOuter) && ((this.frame.height - (this.activeElementPositionTop + this.activeElementHeight)) < this.actionButtonsHeightOuter);
            },
            actionButtonStyles: function() {
                const y = this.activeElementPositionTop + (
                    (this.computedActionsOutside && !this.elementTooTallForActionsOutside) ?
                        (
                            this.positionTooHighForActions ?
                                (this.activeElementHeight + this.actionButtonsPadding)
                                : -(this.actionButtonsHeight + this.actionButtonsPadding)
                        )
                        : this.actionButtonsPadding
                );

                const x = (this.positionTooLeftForActions ) ?
                    this.actionButtonsPadding
                    : this.activeElementPositionLeft + this.activeElementWidth -
                    (
                        this.actionButtonsWidth + (
                            (this.computedActionsOutside && !this.elementTooTallForActionsOutside) ?
                                this.positionTooRightForActions // No horizontal padding when actions are outside the element box, except for what's required to keep "actionButtonsPadding" distance away from right edge of editor frame.
                                : this.actionButtonsPadding
                        )
                    );

                return {
                    transform: `translate(${x}px, ${y}px)`
                };
            },
            actionButtonsShown() {
                return this.active_element > 0;
            }
        },
        watch: {
            active_element: function (val) {
                this.template.elements[val].index = val;
                this.$emit('input', this.template.elements[val]);

                if (val !== -1) {
                    EaseEventBus.fire('expandSettings');
                }

                EaseEventBus.fire('element.' + val + '.temporaryAspectRatio', this.shiftKeyStatus);

                if(val > 0) {
                    this.$nextTick(() => {
                        this.activeElementPositionLeft = +this.template.elements[val].position.left;
                        this.activeElementPositionTop = +this.template.elements[val].position.top;
                    });
                } else {
                    this.actionsResizeObserver.disconnect();
                }
            },
            value: {
                handler: function () {
                    let tmpElement = {};
                    Object.assign(tmpElement, {'index': this.active_element}, this.value);
                    Vue.set(this.template.elements, this.active_element, tmpElement);

                    this.publishElementsList();
                },
                deep: true
            },
            activeUsergroups: {
                handler: function () {
                    this.updateUsergroupSelection();
                },
                deep: true
            },
            templateUsergroupsStrict: function (val) {
                EaseEventBus.fire("usergroupsStrictChanged", val);
            },
            templateLayers: {
                handler: function () {
                    this.updateLayerSelection();
                },
                deep: true
            },
            grid: function () {
                this.updateFrameStyles();
            },
            async actionButtonsShown(val) {
                if (val) {
                    await this.$nextTick();
                    this.actionsResizeObserver.observe(this.$refs.elementActions);
                } else {
                    this.actionsResizeObserver.disconnect();
                }
            },
            canvasClickCooldown(val) {
                if (val) {
                    setTimeout(() => this.canvasClickCooldown = false, 6);
                }
            }
        },
        methods: {
            updateUsergroupSelection: function() {
                this.currentElementUsergroupLimitation = this.template.elements[0].usergroups.map(o => o.toString());
                this.template.elements[this.active_element].usergroups.forEach(o => {
                    if (!this.currentElementUsergroupLimitation.includes(o.toString())) {
                        this.currentElementUsergroupLimitation.push(o.toString());
                    }
                });
                if(this.currentElementUsergroupLimitation.length === 0) {
                    this.currentElementUsergroupLimitation = this.parentUsergroupLimitation.map(o => o.toString());
                }
                if(this.active_element === 0) {
                    EaseEventBus.fire("usergroupsSelectionChanged", this.currentElementUsergroupLimitation);
                }
            },
            updateLayerSelection: function() {
                this.currentLayerLimitation = this.template.layers.map(o => o.toString());
                if(this.currentLayerLimitation.length === 0) {
                    this.currentLayerLimitation = this.parentLayerLimitation.map(o => o.toString());
                }
                EaseEventBus.fire("layersSelectionChanged", this.currentLayerLimitation);
            },
            defaultValue: function (type, field, created) {
                let presetValue = null;
                if(created && this.template.presets[type]) {
                    presetValue = this.template.presets[type]['template'] ? this.template.presets[type]['template'][field] : null;
                    presetValue = presetValue ? presetValue : (this.template.presets[type]['all'] ? this.template.presets[type]['all'][field] : null);
                }

                if(presetValue) {
                    return presetValue;
                } else if (type in elementsSettingsDefaults && field in elementsSettingsDefaults[type]) {
                    return elementsSettingsDefaults[type][field];
                } else if ("default" in elementsSettingsDefaults && field in elementsSettingsDefaults["default"]) {
                    return elementsSettingsDefaults["default"][field];
                }
                return "";
            },
            loadDefinitionForType: function (type) {
                let prefix = "t_";
                if (type === undefined) {
                    return;
                }

                let properties = [];
                if (elementsSettingsDefinition[type] !== undefined && elementsSettingsDefinition[prefix + type] !== undefined) {
                    Object.assign(properties, elementsSettingsDefinition[type], elementsSettingsDefinition[prefix + type]);
                } else if (elementsSettingsDefinition[type] !== undefined) {
                    properties = elementsSettingsDefinition[type];
                } else if (elementsSettingsDefinition[prefix + type] !== undefined) {
                    properties = elementsSettingsDefinition[prefix + type];
                }
                return properties;
            },
            getPropertiesForType: function (type) {
                type = type ?? 'background';
                return this.loadDefinitionForType(type);
            },
            minValue: function (type, field) {
                if (type in elementsSettingsLimits && field in elementsSettingsLimits[type]) {
                    return elementsSettingsLimits[type][field][0];
                } else if ("default" in elementsSettingsLimits && field in elementsSettingsLimits["default"]) {
                    return elementsSettingsLimits["default"][field][0];
                }
                return undefined;
            },
            maxValue: function (type, field) {
                if (type in elementsSettingsLimits && field in elementsSettingsLimits[type]) {
                    return elementsSettingsLimits[type][field][1];
                } else if ("default" in elementsSettingsLimits && field in elementsSettingsLimits["default"]) {
                    return elementsSettingsLimits["default"][field][1];
                }
                return undefined;
            },
            publishElementsList: function () {
                let tmpList = [];

                _.each(this.template.elements, function (element, index) {
                    tmpList.push({
                        index: index,
                        description: element.description || '',
                        type: element.type,
                        name: element.name || ''
                    });
                });

                EaseEventBus.fire('elementList', tmpList);
            },
            updateFrameStyles: function () {
                let oldFrameRatio = this.frame.ratio;
                this.calculateFrameSize(JSON.parse(this.previousParameters));
                this.$refs['editor-frame'].styles = this.frameStyles;

                _.forEach(this.template.elements, (element) => {
                    if (typeof element === 'object') {
                        element.position.top *= this.frame.ratio / oldFrameRatio;
                        element.position.left *= this.frame.ratio / oldFrameRatio;
                        element.size.width *= this.frame.ratio / oldFrameRatio;
                        element.size.height *= this.frame.ratio / oldFrameRatio;
                    }
                });
            },
            processWindowSize: function () {
                let layoutInfo = this.layoutInfo;
                if (layoutInfo) {
                    let w = window.innerWidth;
                    let h = window.innerHeight;

                    let colWidth = (w - 2 * layoutInfo.wrapperLRpadding) / 12;
                    let maxHeight = h - layoutInfo.navBarHeight - layoutInfo.footerHeight;
                    let borderEditorFrame = 2;
                    let maxEditorWidth = Math.max(layoutInfo.minColWidth, colWidth) * this.numColsCanvas - 2*borderEditorFrame;
                    let maxEditorHeight = Math.max(layoutInfo.minHeight, maxHeight) - layoutInfo.actionButtonsHeight - 2*layoutInfo.wrapperTBpadding - 2*borderEditorFrame;
                    if(this.preview) {
                        maxEditorHeight = h;
                        maxEditorWidth = w;
                    }
                    this.computedMaxWidth = (maxEditorWidth);
                    this.computedMaxHeight = (maxEditorHeight);

                    let data = {'colWidth': colWidth, 'contentHeight': maxHeight};
                    EaseEventBus.fire('windowInfo', data);

                    this.updateFrameStyles();
                }
            },
            setLayoutInfo: function () {
                let colLRpadding = 10, editorPadding = colLRpadding;
                let actionButtonsHeight = this.headerHeight ?? 56;
                let navBarHeight = 50, footerHeight = 39, wrapperTBpadding = 10, wrapperLRpadding = 10 - colLRpadding;
                let sumBordersEditorFrame = 0;

                let minColWidth = 50;
                let minHeight = 300;

                let dragdropNavTabsHeight = 50;

                let settingsTitleHeight = this.headerHeight ?? 56;

                let data = {
                    'navBarHeight': navBarHeight,
                    'wrapperTBpadding': wrapperTBpadding,
                    'wrapperLRpadding': wrapperLRpadding,
                    'footerHeight': footerHeight,
                    'colLRpadding': colLRpadding,

                    'minColWidth': minColWidth,
                    'minHeight': minHeight,

                    'settingsTitleHeight': settingsTitleHeight,

                    'dragdropNavTabsHeight': dragdropNavTabsHeight,

                    'actionButtonsHeight': actionButtonsHeight,
                    'editorPadding': editorPadding,
                    'sumBordersEditorFrame': sumBordersEditorFrame,
                };

                this.layoutInfo = data;
            },
            spreadLayoutInfo: function () {
                EaseEventBus.fire('layoutInfo', this.layoutInfo);
            },
            calculateFrameSize: function (data) {
                if (!(data && data.format && data.resolution && this.computedMaxWidth && this.computedMaxHeight)) {
                    return;
                }

                let ratioArray = data.format.split(':'),
                    resolutionArray = data.resolution.split('x'),
                    ratioWidth = parseInt(ratioArray[0]),
                    ratioHeight = parseInt(ratioArray[1]),
                    resolutionWidth = parseInt(resolutionArray[0]),
                    resolutionHeight = parseInt(resolutionArray[1]),
                    parentWidth = $(this.$el).parent().width(),
                    wRatio = ratioHeight / ratioWidth,
                    hRatio = ratioWidth / ratioHeight;

                this.resolutionWidth = resolutionWidth;
                this.resolutionHeight = resolutionHeight;

                if (this.preview !== 'true') {
                    if (resolutionWidth > this.computedMaxWidth) {
                        resolutionHeight = this.computedMaxWidth * resolutionHeight / resolutionWidth;
                        resolutionWidth = this.computedMaxWidth;
                    }
                    if (resolutionHeight > this.computedMaxHeight) {
                        resolutionWidth = this.computedMaxHeight * resolutionWidth / resolutionHeight;
                        resolutionHeight = this.computedMaxHeight;
                    }
                }

                if (this.preview === 'true') {
                    if (resolutionWidth > resolutionHeight) {
                        this.frame.width = parseInt(resolutionWidth);
                        this.frame.height = this.frame.width * wRatio;
                    } else {
                        this.frame.height = parseInt(resolutionHeight);
                        this.frame.width = this.frame.height / wRatio;
                    }
                } else {
                    let preferredWidth = true;

                    if (resolutionHeight > resolutionWidth) {
                        preferredWidth = false;
                    }

                    this.frame.height = resolutionHeight;

                    if (preferredWidth === true) {
                        this.frame.width = this.frame.height * hRatio;
                    } else {
                        this.frame.width = this.frame.height / wRatio;
                    }

                    if (this.frame.width > parentWidth && preferredWidth === true) {
                        this.frame.width = parentWidth - 4; // 2px padding/outline each side
                        this.frame.height = this.frame.width * wRatio;
                    }
                }
                this.frame.width *= this.zoom;
                this.frame.height *= this.zoom;
                this.frame.ratio = this.frame.width / this.resolutionWidth;
                this.frame.default_size = 600 * this.frame.ratio;

                this.stepHorizontal = (this.frame.width/100);
                this.stepVertical = (this.frame.height/100);

                this.$nextTick(() => {
                    EaseEventBus.fire('frameInfos', this.frame);
                });
            },
            is_enabled: function (element) {
                return this.advancedPrivilege && !element.locked && !element.readonly;
            },
            activeIcons: function (element) {
                return !this.preview && this.is_enabled(element) && element.type !== 'background';
            },
            addElementToCanvas: function (data, dragDrop=false, copy=false, structure=null) {
                if (structure === null) {
                    if (data.type === 'other') {
                        toaster.error(Lang.langGet("Elements of this type cannot be added."));
                        return;
                    }
                    if (dragDrop || copy) {
                        EaseEventBus.fire("unsavedChanges", true);
                    }

                    structure = {};
                    structure.name = typeof data.name !== 'undefined' ? data.name : '';
                    structure.position = {};
                    structure.size = {};
                    structure.state = 'unknown';

                    this.calculateHighestZindex();

                    if (typeof data.position.zindex !== 'undefined') {
                        structure.position.zindex = data.position.zindex;
                    } else {
                        structure.position.zindex = this.highest_zindex + 1;
                        this.highest_zindex = structure.position.zindex;
                    }

                    structure.type = data.type;
                    structure.id = typeof data.id !== 'undefined' ? data.id : 0;


                    var widthFactor = this.resolutionWidth;
                    var heightFactor = this.resolutionHeight;
                    if (dragDrop || copy) {
                        var absoluteDataLeft = data.position.left === 'undefined' ? 'undefined' : data.position.left;
                        var absoluteDataTop = data.position.top === 'undefined' ? 'undefined' : data.position.top;
                        var absoluteDataWidth = data.size.width === 'undefined' ? 'undefined' : data.size.width;
                        var absoluteDataHeight = data.size.height === 'undefined' ? 'undefined' : data.size.height;
                    } else {
                        var absoluteDataLeft = data.position.left === 'undefined' ? 'undefined' : data.position.left * widthFactor;
                        var absoluteDataTop = data.position.top === 'undefined' ? 'undefined' : data.position.top * heightFactor;
                        var absoluteDataWidth = data.size.width === 'undefined' ? 'undefined' : data.size.width * widthFactor;
                        var absoluteDataHeight = data.size.height === 'undefined' ? 'undefined' : data.size.height * heightFactor;
                    }

                    switch (data.type) {
                        case 'youtube':
                            structure.size.ratio = this.frame.default_ratio;
                            break;
                        case 'qrcode':
                            structure.size.ratio = 1;
                            break;
                        case 'picture':
                        case 'video':
                        case 'template':
                        case 'campaign':
                            if (typeof data.size.ratio === 'undefined') {
                                structure.size.ratio = absoluteDataHeight / absoluteDataWidth;
                            } else {
                                structure.size.ratio = data.size.ratio;
                            }
                            break;
                        default:
                            structure.size.ratio = 0;
                    }

                    if (copy) {
                        structure.position.left = 0;
                        structure.position.top = 0;
                        structure.size.width = data.size.width;
                        structure.size.height = data.size.height;
                        structure.size.ratio = data.size.ratio;
                    } else {

                        if (typeof absoluteDataTop !== 'undefined' && typeof absoluteDataLeft !== 'undefined') {
                            structure.position.top = absoluteDataTop;
                            structure.position.left = absoluteDataLeft;
                        } else {
                            structure.position.top = this.frame.default_position;
                            structure.position.left = this.frame.default_position;
                        }

                        if (typeof data.overwrite_size !== 'undefined') {
                            structure.size.width = absoluteDataWidth;
                            structure.size.height = absoluteDataHeight;
                        } else {
                            if (absoluteDataWidth * this.frame.ratio> this.frame.default_size) {
                                structure.size.width = this.frame.default_size;
                                structure.size.height = this.frame.default_size * structure.size.ratio;

                                if (structure.size.height === 0) {
                                    structure.size.height = this.frame.default_size;
                                }
                            } else {
                                if (absoluteDataWidth > 0 && absoluteDataHeight > 0) {
                                    structure.size.width = absoluteDataWidth * this.frame.ratio;
                                    structure.size.height = absoluteDataHeight * this.frame.ratio;
                                } else {
                                    structure.size.width = this.frame.default_size;

                                    if (structure.size.ratio === 0) {
                                        structure.size.height = this.frame.default_size * this.frame.default_ratio;
                                    } else {
                                        structure.size.height = this.frame.default_size * structure.size.ratio;
                                    }
                                }
                            }

                            if (structure.size.height > this.frame.height) {
                                structure.size.height = this.frame.height;
                                if (structure.size.ratio === 0) {
                                    structure.size.width = structure.size.height / this.frame.default_ratio;
                                } else {
                                    structure.size.width = structure.size.height / structure.size.ratio;
                                }
                            }
                        }
                        if (dragDrop) {
                            structure.position.left = Math.max(0, Math.min(structure.position.left - structure.size.width / 2, this.frame.width - structure.size.width));
                            structure.position.top = Math.max(0, Math.min(structure.position.top - structure.size.height / 2, this.frame.height - structure.size.height));
                        }
                    }

                    if (_.indexOf(['picture', 'video', 'pdf', 'ppt', 'template', 'campaign', 'widget'], data.type) !== -1) {
                        structure.background_image = data.background_image;
                    } else {
                        structure.background_image = '';
                    }

                    if (_.indexOf(['pdf'], data.type) !== -1) {
                        structure.page_count = data.page_count;
                    }
                    if (_.indexOf(['picture', 'video', 'pdf', 'ppt'], data.type) !== -1) {
                        structure.height = data.height;
                        structure.width = data.width;
                    }

                    //FILL VALUES
                    if (data.type === 'widget') {
                        for (const key of Object.keys(data)) {
                            if (!structure.hasOwnProperty(key)) {
                                structure[key] = data[key];
                            }
                        }
                    }

                    for (let [key, def] of Object.entries(this.getPropertiesForType(data.type))) {
                        if (def.computed) {
                            key = def.computed;
                        }

                        if (typeof _.get(structure, key) === 'undefined') {
                            let value = (typeof _.get(data, key) !== 'undefined' && _.get(data, key) !== null) ? _.get(data, key) : this.defaultValue(data.type, key, dragDrop);
                            _.set(structure, key, value);
                        }
                    }

                    if (data.type === 'campaign') {
                        structure.dynamic = typeof data.dynamic !== 'undefined' ? data.dynamic : this.defaultValue(data.type, "dynamic", dragDrop || copy);
                    }

                    structure.readonly = typeof data.readonly !== 'undefined' ? data.readonly : this.defaultValue(data.type, "readonly", dragDrop || copy);
                    structure.unique_id = typeof data.unique_id !== 'undefined' ? data.unique_id : this.defaultValue(data.type, "unique_id", dragDrop || copy);


                    if (data.state === 'link' || data.state === 'copy') {
                        structure.state = data.state;
                    }

                    if (data.type === 'layer') {
                        let nr = -1;
                        let index = _.findIndex(this.all_layers, function (o) {
                            return String(o.value) === structure.layer;
                        });

                        if (typeof this.all_layers[index] !== 'undefined') {
                            nr = this.all_layers[index].nr;
                        }

                        if (nr !== -1) {
                            if (Laravel.switch_off_times === true && nr === 0) {
                                structure.layer = '';
                            }
                        }

                        structure.all_layers = this.all_layers;
                    }

                    structure.index = this.template.elements.length + this.queued_structures.length;

                    if (structure.type === 'widget' && (copy || dragDrop)) {
                        if (copy) {
                            structure['query_string'] = 'COPY';
                        } else if (dragDrop) {
                            structure['query_string'] = 'CREATED';
                        }
                        this.queued_structures.push(structure.index);
                        EaseEventBus.fire('newStructure_SetWidgetQuery', {default_query: dragDrop, structure: structure});
                        setTimeout(this.addElementToCanvas, 1000, null, dragDrop, copy, structure);
                        return;
                    }
                } else {
                    let index = this.queued_structures.indexOf(structure.index);
                    if (index === -1) {
                        return;
                    } else {
                        this.queued_structures.splice(index, 1);
                    }
                }

                this.template.elements.push(structure);
                this.active_element = structure.index;
                this.$nextTick(() => {
                    EaseEventBus.fire('tap', {index: this.active_element, type: structure.type});
                    EaseEventBus.fire('reinitializeDragula');
                });
            },
            calculateHighestZindex: function () {
                let tmpZindex = 0;
                _.forEach(this.template.elements, function (data) {
                    if (data.position.zindex > tmpZindex) {
                        tmpZindex = data.position.zindex;
                    }
                });

                this.highest_zindex = tmpZindex;
            },
            replaceElementInCanvas: function (data) {
                this.template.elements[this.active_element].id = data.id;
                if(this.active_element !== 0) {
                    this.template.elements[this.active_element].name = data.name;
                }
                if (_.indexOf(['picture', 'video'], data.type) !== -1) {
                    this.template.elements[this.active_element].background_image = data.background_image;
                }
                this.template.elements[this.active_element].width = data.width;
                this.template.elements[this.active_element].height = data.height;
                this.template.elements[this.active_element].page_count = String(data.page_count);

                this.$forceUpdate();
                this.$emit('input', this.template.elements[this.active_element]);
            },
            alternativeElementUpdate: function (data) {
                this.template.elements[this.active_element].alternative_id = data.id;
                this.template.elements[this.active_element].alternative_type = data.type;
                this.template.elements[this.active_element].alternative_background_image = data.url;
                this.template.elements[this.active_element].alternative_name = data.name;

                this.$forceUpdate();
                this.$emit('input', this.template.elements[this.active_element]);
            },
            loadElementsToCanvas: function (data) {
                let that = this;
                let sortData = _.sortBy(data, ['position.zindex']);

                // normalize zindex
                _.forEach(sortData, (element, key) => {
                    if (typeof element === 'object') {
                        // regarding +2: background element with zindex = 1 already added at initial data
                        element.position.zindex = key + 2;
                    }
                });

                _.forEach(sortData, (element) => {
                    if (typeof element === 'object') {
                        element.overwrite_size = true;
                        element.position.top *= this.frame.ratio;
                        element.position.left *= this.frame.ratio;
                        element.size.width *= this.frame.ratio;
                        element.size.height *= this.frame.ratio;

                        let offWidth = element.position.left + element.size.width,
                            offHeight = element.position.top + element.size.height,
                            ratio = element.size.width / element.size.height;

                        if (element.stretch !== 'original') {
                            ratio = element.size.ratio;
                        }

                        if (offWidth > this.frame.width || offHeight > this.frame.height) {
                            element.position.top = 0;
                            element.position.left = 0;
                            element.size.width = this.frame.default_size;
                            element.size.height = element.size.width / ratio;
                        }

                        this.addElementToCanvas(element);
                    }
                });
            },
            removeSelectedElement: function () {
                if (this.advancedPrivilege && this.template.elements[this.active_element].readonly === false) {
                    EaseEventBus.fire("unsavedChanges", true);
                    if (this.active_element === 0) {
                        this.template.elements[0].background_image = '';
                    } else {
                        EaseEventBus.fire("removedElement", this.active_element);
                        this.template.elements.splice(this.active_element, 1);
                        this.clearActiveElement();
                    }
                    this.active_element = 0;
                    EaseEventBus.fire('tap', {index: 0});
                    this.calculateHighestZindex();
                }
            },
            clearActiveElement: function () {
                this.active_element = 0;
            },
            resetToDefaultSize: function () {
                let ratio = this.template.elements[this.active_element].size.ratio;

                if (ratio < 1) {
                    this.template.elements[this.active_element].size.height = this.frame.default_size * ratio;
                } else {
                    this.template.elements[this.active_element].size.height = this.frame.default_size / ratio;
                }

                this.template.elements[this.active_element].size.width = this.frame.default_size;
            },
            updateTemplateWrapper(data) {
                if (data.publish && !localStorage.getItem("template-autopublish-confirm-" + Laravel.activeProject)) {
                    EaseEventBus.fire('showAutopublishWarning', {data: data, type: 'template', task: 'Save'});
                } else {
                    this.updateTemplate(data);
                }
            },
            updateTemplate: function (data) {
                axios.post('/content/template/edit/' + this.template.id + '/update', {
                    name: this.template.elements[0].name,
                    project: Laravel.activeProject,
                    previousParameters: this.previousParameters,
                    template: this.template,
                    ratio: this.frame.ratio,
                    inline: this.inline,
                    link_id: this.link_id,
                    add: this.add,
                    insertX: this.insertX,
                    insertY: this.insertY,
                    approve: data.approve ?? 0,
                    publish: data.publish ?? 0,
                }).then((response) => {
                    EaseEventBus.fire("unsavedChanges", false);
                    if (data.refresh === true) {
                        window.location = '/content/template/edit/' + response.data.data.id;
                    } else {
                        axios.post('/content/inline-info/store', {
                            parentType: "template",
                            parentLayerLimitation: JSON.stringify(this.template.layers),
                            parentUsergroupLimitation: JSON.stringify(this.currentElementUsergroupLimitation),
                        }).then(() => {
                            if (_.isBoolean(data.back)) {
                                EaseEventBus.fire('doneUpdatingTemplate', data.back);
                            } else {
                                window.location = data.back;
                            }
                        });
                    }
                }).catch((error) => {
                    EaseEventBus.fire('doneUpdatingTemplate', 'error');

                    if (error.response.status === 401) {
                        window.location = '/login';
                    } else if (error.response.status === 422) {
                        let errorString = this.t_edit_failed_prefix + ' ';

                        for (let key in error.response.data) {
                            errorString = errorString + error.response.data[key][0] + ' ';
                        }

                        let escapedString = $("<div>").text(errorString).html();
                        toaster.error(escapedString);
                    } else {
                        toaster.error(this.t_edit_failed_prefix + ' '+ error.response?.statusText);
                    }
                });
            },
            previewTemplate: function () {
                axios.post('/content/preview', {
                    elements: this.template.elements,
                    type: 'template',
                    parameters: this.template.parameters,
                    ratio: this.frame.ratio,
                    id: this.template.id
                }).then((response) => {
                    this.preview_url = response.data.data.url;
                    EaseEventBus.fire('showContentPreview');
                    EaseEventBus.fire('donePreviewing');
                }).catch((error) => {
                    if (error.response.status === 401) {
                        window.location = '/login';
                    }

                    toaster.error(this.t_preview_failed);
                });
            },
            copyLocalCopy: function (element) {
                axios.post('/content/' + element.type + '/copy-local', {
                    project: Laravel.activeProject,
                    id: element.id
                }).then((response) => {
                    EaseEventBus.fire("unsavedChanges", true);
                    this.calculateHighestZindex();
                    let newID = response.data;

                    this.$nextTick(() => {
                        element.background_image = _.replace(element.background_image, element.id, newID);
                        element.id = newID;
                        element.position.zindex = this.highest_zindex + 1;
                        element.position.left = element.position.top = 0;
                        element.unique_id = null;
                        this.template.elements.push(element);
                    });
                }).catch((error) => {
                    if (error.response.status === 401) {
                        window.location = '/login';
                    } else if (error.response.status === 403) {
                        toaster.error(this.t_not_enough_privileges);
                    } else {
                        toaster.error(this.t_add_failed);
                    }
                });
            },
            handleTemplateInlineAdd: function (data, updateStructure) {
                axios.post('/content/template/inline-add', {
                    project: Laravel.activeProject,
                    name: 'not-in-use',
                    width: 1,
                    height: 1,
                    resolution: this.template.parameters.resolution,
                    format: this.template.parameters.format
                }).then((response) => {
                    updateStructure.back = '/content/template/edit-inline/' + response.data.data.id + '?add=inline&x=' + data.x + '&y=' + data.y;
                    this.updateTemplateWrapper(updateStructure);
                }).catch(() => {
                    toaster.error(this.t_add_failed);
                });
            },
            handleCampaignInlineAdd: function (data, updateStructure) {
                axios.post('/content/campaign/inline-add', {
                    project: Laravel.activeProject,
                    name: 'not-in-use',
                    width: 1,
                    height: 1,
                    resolution: this.template.parameters.resolution,
                    format: this.template.parameters.format
                }).then((response) => {
                    updateStructure.back = '/content/campaign/edit-inline/' + response.data.data.id + '?add=inline&x=' + data.x + '&y=' + data.y;
                    this.updateTemplateWrapper(updateStructure);
                }).catch((error) => {
                    toaster.error(this.t_add_failed);
                });
            },
            keyEvent(e, keyEventType) {
                if (this.sendKeyEvent) {
                    if (!e.shiftKey) {
                        if (e.key === "ArrowLeft") {
                            EaseEventBus.fire('element.' + this.active_element + '.move', [[-this.stepHorizontal, 0], keyEventType]);
                        } else if (e.key === "ArrowRight") {
                            EaseEventBus.fire('element.' + this.active_element + '.move', [[this.stepHorizontal, 0], keyEventType]);
                        } else if (e.key === "ArrowUp") {
                            EaseEventBus.fire('element.' + this.active_element + '.move', [[0, -this.stepVertical], keyEventType]);
                        } else if (e.key === "ArrowDown") {
                            EaseEventBus.fire('element.' + this.active_element + '.move', [[0, this.stepVertical], keyEventType]);
                        }
                    } else {
                        if (e.key === "ArrowLeft") {
                            EaseEventBus.fire('element.' + this.active_element + '.resize', [[-this.stepHorizontal, 0], keyEventType]);
                        } else if (e.key === "ArrowRight") {
                            EaseEventBus.fire('element.' + this.active_element + '.resize', [[this.stepHorizontal, 0], keyEventType]);
                        } else if (e.key === "ArrowUp") {
                            EaseEventBus.fire('element.' + this.active_element + '.resize', [[0, -this.stepVertical], keyEventType]);
                        } else if (e.key === "ArrowDown") {
                            EaseEventBus.fire('element.' + this.active_element + '.resize', [[0, this.stepVertical], keyEventType]);
                        }
                    }
                }
                if (e.key === "Shift") {
                    this.shiftKeyStatus = e.shiftKey;
                    if (this.sendKeyEvent) {
                        EaseEventBus.fire('element.' + this.active_element + '.temporaryAspectRatio', e.shiftKey);
                    }
                }
            },
            updateTemplateAndAdd: function (data) {
                let updateStructure = {
                    refresh: false
                };

                switch (data.type) {
                    case 'mediaplaceholder':
                        updateStructure.back = '/content/mediapool?x=' + data.x + '&y=' + data.y + '&add=' + data.type.replace('placeholder', '');
                        this.updateTemplateWrapper(updateStructure);
                        break;
                    case 'templateplaceholder':
                        this.handleTemplateInlineAdd(data, updateStructure);
                        break;
                    case 'campaignplaceholder':
                        this.handleCampaignInlineAdd(data, updateStructure);
                        break;
                }
            },
            onElementActionsResize(entries) {
                for (const entry of entries) {
                    const contentBoxSize = entry.borderBoxSize[0];
                    this.actionButtonsWidth = contentBoxSize.inlineSize;
                    this.actionButtonsHeight = contentBoxSize.blockSize;
                }
            },
            isElementInteractive(index) {
                if (!this.ctrlDrilledMode) return true;

                return this.template.elements[this.active_element].position.zindex >= this.template.elements[index].position.zindex;
            },
            onCanvasCtrlClick(event) {
                if(this.canvasClickCooldown) {
                    return;
                }

                // Get canvas bounding rect
                const rect = this.$refs.editorCanvas.getBoundingClientRect();

                // Calculate coordinates relative to the canvas
                const canvasX = event.clientX - rect.left;
                const canvasY = event.clientY - rect.top;

                const activeElementZindex = this.template.elements[this.active_element].position.zindex;

                const sortedTemplateElements = this.template.elements
                    .slice() // Make a shallow array copy
                    .sort((a, b) => b.position.zindex - a.position.zindex) // Sort by z-index, descending

                let selectedElementBelow;

                for (const element of sortedTemplateElements) {
                    if (element.position.zindex >= activeElementZindex) continue;
                    if ((canvasX < element.position.left) || (canvasX > (element.position.left + element.size.width))) continue;
                    if ((canvasY < element.position.top) || (canvasY > (element.position.top + element.size.height))) continue;
                    selectedElementBelow = element;
                    break;
                }

                if (selectedElementBelow) {
                    const selectedElementBelowIndex = this.template.elements.indexOf(selectedElementBelow);
                    if (selectedElementBelowIndex >= 0) {
                        this.ctrlDrilledMode = true;
                        this.active_element = selectedElementBelowIndex;
                        EaseEventBus.fire('tap', {index: this.active_element});
                    }
                } else {
                    this.ctrlDrilledMode = false;
                    this.active_element = 0;
                    EaseEventBus.fire('tap', {index: this.active_element});
                }
            },
            onCanvasClick(event) {
                if(event.ctrlKey) return;
                if(this.ctrlDrilledMode && !this.active_element) {
                    this.ctrlDrilledMode = false;
                }
            },
            onElementEditButtonClick() {
                EaseEventBus.fire('onElementEditButtonClick');
            }
        },
        created() {
            EaseEventBus.listen('elementEditButtonShown', (val) => this.elementEditButtonShown = val);
        },
        mounted() {
            let json = JSON.parse(this.data);
            let elements = null;

            try {
                elements = JSON.parse(json.elements);
                json.parameters = JSON.parse(json.parameters);
            } catch (e) {
                /*
                    If an error occurs then json.parameters is already an object and nothing needs to be done.
                    Similar for json.elements.
                */
                elements = json.elements;
            }

            this.previousParameters = JSON.stringify(json.parameters);

            json.elements = this.template.elements;

            if (Object.keys(json.parameters).length === 0) {
                json.elements[0].parameters = {
                    resolution: '',
                    format: '',
                    'valid-from': '',
                    'valid-to': ''
                };
            } else {
                json.elements[0].parameters = json.parameters;
            }


            this.setLayoutInfo();
            this.processWindowSize();
            this.$nextTick(() => {
                this.spreadLayoutInfo();
                this.processWindowSize();
            })

            // Set background data (used for template data)
            if (elements.length > 0) {
                json.elements[0].background_image = elements[0].background_image;
                json.elements[0].readonly = elements[0].readonly;
                json.elements[0].unique_id = elements[0].unique_id;

                elements = _.slice(elements, 1);
            }

            json.elements[0].size.width = this.frame.width;
            json.elements[0].size.height = this.frame.height;
            json.elements[0].name = json.name;
            json.elements[0].id = 0;
            json.elements[0].stretch = 'original';
            json.elements[0].usergroups = json.usergroups;
            json.elements[0].strict_mode = json.strict_mode;
            json.elements[0].locked = json.locked;
            json.elements[0].layers = json.layers;
            json.elements[0].tags = json.tags;
            this.link_id = json['id_link'];
            this.all_layers = json['all_layers'];

            if (Object.keys(json).length > 0) {
                this.template = _.extend({}, this.template, json);
            }

            // Synchronize template data to settings
            this.template.elements[0].index = 0;
            this.$emit('input', this.template.elements[0]);

            this.loadElementsToCanvas(elements);
            this.active_element = 0;
            EaseEventBus.fire('tap', {index: this.active_element, type: this.template.elements[this.active_element].type});

            if(this.parent_layer_limitation) {
                this.parentLayerLimitation = JSON.parse(this.parent_layer_limitation);
            }
            if(this.parent_usergroup_limitation) {
                this.parentUsergroupLimitation = JSON.parse(this.parent_usergroup_limitation);
            }
            this.currentElementUsergroupLimitation = this.parentUsergroupLimitation.map(o => o.toString());
            this.currentLayerLimitation = this.parentLayerLimitation.map(o => o.toString());

            EaseEventBus.listen('addElementToCanvas', debounce((data) => {
                this.addElementToCanvas(data);
            }, 200));

            EaseEventBus.listen('addElementToCanvasDragDrop', debounce((data) => {
                this.addElementToCanvas(data, true);
            }, 200));

            EaseEventBus.listen('replaceElementInCanvas', (data) => {
                this.replaceElementInCanvas(data);
            });
            EaseEventBus.listen('alternativeElementUpdate', (data) => {
                this.alternativeElementUpdate(data);
            });

            EaseEventBus.listen('elementMoved', (data) => {
                if(data.id != this.active_element) {
                    return;
                }
                this.activeElementPositionLeft = data.x;
                this.activeElementPositionTop = data.y;
            });

            EaseEventBus.listen('elementMovedEnd', (data) => {
                this.template.elements[data.id].position.left = this.activeElementPositionLeft;
                this.template.elements[data.id].position.top = this.activeElementPositionTop;

                this.canvasClickCooldown = true;
            });

            EaseEventBus.listen('zindexUp', (data) => {
                let elements = this.template.elements,
                    currentZindex = this.template.elements[data.id].position.zindex;
                _.map(elements, (element, key) => {
                    element.filter_index = key;
                });

                let filtered = _.filter(elements, function (element) {
                    return element.position.zindex > currentZindex;
                });

                if (filtered.length > 0) {
                    let ordered = _.orderBy(filtered, ['position.zindex'], ['asc']);

                    this.template.elements[ordered[0].filter_index].position.zindex = currentZindex;
                    this.template.elements[data.id].position.zindex = currentZindex + 1;
                }
            });

            EaseEventBus.listen('zindexDown', (data) => {
                let elements = this.template.elements,
                    currentZindex = this.template.elements[data.id].position.zindex;
                _.map(elements, (element, key) => {
                    element.filter_index = key;
                });

                let filtered = _.filter(elements, function (element) {
                    return element.position.zindex > 1 && element.position.zindex < currentZindex;
                });

                if (filtered.length > 0) {
                    let ordered = _.orderBy(filtered, ['position.zindex'], ['desc']);
                    let lowerIndex = this.template.elements[ordered[0].filter_index].position.zindex;

                    this.template.elements[data.id].position.zindex = lowerIndex;
                    this.template.elements[ordered[0].filter_index].position.zindex = lowerIndex + 1;
                }
            });

            EaseEventBus.listen('copyElement', (data) => {
                let element = JSON.parse(JSON.stringify(this.template.elements[data.id]));
                element.index = this.template.elements.length;

                if (_.indexOf(['template', 'campaign'], element.type) !== -1 && element.state === 'copy') {
                    this.copyLocalCopy(element);
                } else {
                    element.position.left = element.position.top = 0;
                    this.calculateHighestZindex();
                    element.position.zindex = this.highest_zindex + 1;
                    element.unique_id = null;

                    this.addElementToCanvas(element, false, true)
                }
            });

            EaseEventBus.listen('deleteElement', (data) => {
                this.active_element = data['id'];
                this.removeSelectedElement();
            });

            EaseEventBus.listen('elementResized',(data) => {
                if(data.id != this.active_element) {
                    return;
                }
                this.activeElementWidth = data.width;
                this.activeElementHeight = data.height;
            });

            EaseEventBus.listen('elementResizedEnd',(data) => {
                this.template.elements[data.id].size.width = this.activeElementWidth;
                this.template.elements[data.id].size.height = this.activeElementHeight;

                this.canvasClickCooldown = true;
            });

            EaseEventBus.listen('resetToDefaultSize', () => {
                //this.resetToDefaultSize();
            });

            EaseEventBus.listen('updateTemplate', (data) => {
                this.updateTemplateWrapper(data);
            });
            EaseEventBus.listen('updateProceed', (data) => {
                if (data.type === 'template' && data.task === 'Save') {
                    if (data.proceed) {
                        this.updateTemplate(data.data);
                    } else {
                        EaseEventBus.fire('doneUpdatingTemplate', 'error');
                    }
                }
            });

            EaseEventBus.listen('updateTemplateAndAdd', (data) => {
                this.updateTemplateAndAdd(data);
            });
            EaseEventBus.listen('newContent', (media_type) => {
                this.updateTemplateAndAdd({type: media_type, x: 0, y: 0});
            });

            EaseEventBus.listen('zoomEditorFrame', (zoom) => {
                this.zoom = zoom;
                this.updateFrameStyles();
            });

            EaseEventBus.listen('previewTemplate', () => {
                this.previewTemplate();
            });

            EaseEventBus.listen('hidePreview', () => {
                this.preview_url = '';
            });

            EaseEventBus.listen('selectViaDropdown', (index) =>  {
                this.active_element = index;
            });

            EaseEventBus.listen('layersSelectionChanged', (data) => {
                if(JSON.stringify(data) !== JSON.stringify(this.template.layers)) {
                    this.template.layers = data;
                }
            });
            EaseEventBus.listen('tagsSelectionChanged', (data) => {
                if(JSON.stringify(data) !== JSON.stringify(this.template.tags)) {
                    this.template.tags = data;
                }
            });
            EaseEventBus.listen('usergroupsSelectionChanged', (data) => {
                if(this.active_element == 0) {
                    this.template.usergroups = data;
                }
            });

            let combokeys = new Combokeys(document.documentElement);
            combokeys.bind(['backspace', 'del'], this.removeSelectedElement);

            this.$nextTick(() => {
                window.addEventListener('resize', this.processWindowSize);
            });

            window.addEventListener('beforeunload', (e) => {
                if(this.unsavedChanges) {
                    e.returnValue = 'Are you sure you want to leave?';
                }
            });

            EaseEventBus.listen("unsavedChanges", (changes) => {
                this.unsavedChanges = changes;
            });

            EaseEventBus.listen('newStructure_AddedWidgetQuery', (data) => {
                this.addElementToCanvas(null, false, false, data.structure);
            })

            window.addEventListener("keypress", (e) => {
                this.keyEvent(e, 'press');
            });

            window.addEventListener("keyup", (e) => {
                this.keyEvent(e, 'up');
            });

            window.addEventListener("keydown", (e) => {
                this.keyEvent(e, 'down');
            });

            window.addEventListener("click", (e) => {
                this.sendKeyEvent = this.$el === e.target || this.$el.contains(e.target);
            });

            this.actionsResizeObserver = new ResizeObserver(this.onElementActionsResize);
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.processWindowSize);
            this.actionsResizeObserver?.disconnect();
        }
    }
</script>
